import { useState, useRef, useEffect } from 'react';
import { Menu, Sun, Moon, Monitor, ChevronDown, LogOut, User, Building2, CalendarRange, MapPin, Layers, RefreshCw, Newspaper } from 'lucide-react';
import { AppRoutes } from '@/constants/routes';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Breadcrumb } from './Breadcrumb';
import type { RootState } from '@/state/store';
import { setTheme } from '@/state/themeSlice';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAuth } from '@/hooks/useAuth';
import { ContextSwitcherModal } from './ContextSwitcherModal';

interface TopBarProps {
  sidebarOpen: boolean;
  sidebarWidth: number;
  collapsedWidth: number;
  onToggleSidebar: () => void;
}

const ThemeToggle = () => {
  const dispatch = useAppDispatch();
  const { mode } = useSelector((s: RootState) => s.theme);

  const cycle = () => {
    const next = mode === 'light' ? 'dark' : mode === 'dark' ? 'system' : 'light';
    dispatch(setTheme(next));
  };

  const Icon = mode === 'dark' ? Moon : mode === 'light' ? Sun : Monitor;
  const label = mode === 'dark' ? 'Dark mode' : mode === 'light' ? 'Light mode' : 'System mode';

  return (
    <button
      onClick={cycle}
      aria-label={`Switch theme (currently ${label})`}
      title={label}
      className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
    >
      <Icon size={18} />
    </button>
  );
};

const UserMenu = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!user) return null;

  const initials = `${user.firstName[0] ?? ''}${user.lastName[0] ?? ''}`.toUpperCase();

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      >
        {user.profileImageUrl ? (
          <img
            src={user.profileImageUrl}
            alt={user.fullName}
            className="w-7 h-7 rounded-full object-cover flex-shrink-0 border border-gray-200 dark:border-gray-600"
          />
        ) : (
          <div className="w-7 h-7 rounded-full bg-brand-600 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
            {initials}
          </div>
        )}
        <div className="hidden sm:block text-left">
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 leading-tight">
            {user.fullName}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 leading-tight">
            {user.roles[0] ?? 'User'}
          </div>
        </div>
        <ChevronDown size={14} className="text-gray-400 hidden sm:block" />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-56 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg z-50 py-1 animate-fade-in">
          <div className="px-3 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2.5">
            {user.profileImageUrl ? (
              <img
                src={user.profileImageUrl}
                alt={user.fullName}
                className="w-9 h-9 rounded-full object-cover flex-shrink-0 border border-gray-200 dark:border-gray-600"
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-brand-600 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                {initials}
              </div>
            )}
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{user.fullName}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
            </div>
          </div>
          <button
            onClick={() => { setOpen(false); navigate(`/admin/users/${user.userId}`); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <User size={15} />
            My Profile
          </button>
          <button
            onClick={() => { setOpen(false); navigate(AppRoutes.DAILY_BRIEFING); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <Newspaper size={15} />
            Daily Briefing
          </button>
          <div className="mx-2 my-1 border-t border-gray-100 dark:border-gray-700" />
          <button
            onClick={() => { setOpen(false); signOut(); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          >
            <LogOut size={15} />
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
};

// ── ERP Context Strip ─────────────────────────────────────────────────────────
const ContextChip = ({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: string | null | undefined;
}) => {
  if (!value) return null;
  return (
    <div className="flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400">
      <Icon size={11} className="flex-shrink-0 text-gray-400 dark:text-gray-500" />
      <span className="text-gray-400 dark:text-gray-600 hidden sm:inline">{label}:</span>
      <span className="font-semibold text-gray-700 dark:text-gray-300 truncate max-w-[120px]">{value}</span>
    </div>
  );
};

const ContextSep = () => (
  <span className="text-gray-300 dark:text-gray-700 text-[11px] select-none hidden sm:inline">|</span>
);

const ERPContextStrip = ({ paddingLeft, onSwitch }: { paddingLeft: number; onSwitch: () => void }) => {
  const session = useSelector((s: RootState) => s.session.session);
  if (!session) return null;

  const hasAny = session.companyName || session.financialYearCode || session.locationName || session.costCenterName;
  if (!hasAny) return null;

  return (
    <div
      style={{ paddingLeft, top: '56px' }}
      className="fixed left-0 right-0 z-10 h-7 bg-slate-50/95 dark:bg-slate-900/90 border-b border-slate-200/80 dark:border-slate-800 transition-[padding-left] duration-200"
    >
      <div className="h-full flex items-center gap-2 px-4 overflow-x-auto scrollbar-none">
        <ContextChip icon={Building2}    label="Co"     value={session.companyName}       />
        {session.financialYearCode && <><ContextSep /><ContextChip icon={CalendarRange} label="FY" value={session.financialYearCode} /></>}
        {session.financialPeriodCode && <><ContextSep /><ContextChip icon={CalendarRange} label="Per" value={session.financialPeriodCode} /></>}
        {session.locationName && <><ContextSep /><ContextChip icon={MapPin} label="Loc" value={session.locationName} /></>}
        {session.costCenterName && <><ContextSep /><ContextChip icon={Layers} label="CC" value={session.costCenterName} /></>}

        {/* Switch button */}
        <div className="ml-auto flex-shrink-0">
          <button
            onClick={onSwitch}
            title="Switch context"
            className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
          >
            <RefreshCw size={10} />
            <span className="hidden sm:inline">Switch</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export const TopBar = ({ sidebarOpen, sidebarWidth, collapsedWidth, onToggleSidebar }: TopBarProps) => {
  const session        = useSelector((s: RootState) => s.session.session);
  const hasStrip       = !!(session?.companyName || session?.financialYearCode || session?.locationName || session?.costCenterName);
  const leftPad        = sidebarOpen ? sidebarWidth : collapsedWidth;
  const [switcherOpen, setSwitcherOpen] = useState(false);

  return (
    <>
      <header
        style={{ paddingLeft: leftPad }}
        className="fixed top-0 right-0 left-0 z-20 h-14 bg-white dark:bg-slate-900 border-b border-slate-200/80 dark:border-slate-800 transition-[padding-left] duration-200"
      >
        <div className="h-full flex items-center gap-3 px-4">
          {/* Hamburger */}
          <button
            onClick={onToggleSidebar}
            aria-label="Toggle sidebar"
            className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex-shrink-0 lg:hidden"
          >
            <Menu size={18} />
          </button>

          {/* Breadcrumb */}
          <div className="flex-1 min-w-0">
            <Breadcrumb />
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <ThemeToggle />

            {/* Notifications */}
            <NotificationBell />

            {/* Divider */}
            <div className="w-px h-5 bg-gray-200 dark:bg-gray-700 mx-1" />

            <UserMenu />
          </div>
        </div>
      </header>

      {/* Context strip sits immediately below the main header */}
      {hasStrip && <ERPContextStrip paddingLeft={leftPad} onSwitch={() => setSwitcherOpen(true)} />}

      {/* Context switcher modal */}
      <ContextSwitcherModal open={switcherOpen} onClose={() => setSwitcherOpen(false)} />
    </>
  );
};
